import type { PageComponentMetaWithPosition } from '@/core/features/a-dynamic-page/dynamic-page-pacts/dynamic-page-type';
import type { PageComponentProps } from '@/features/a-dynamic-page/components/page-component-switch';
import type { SpotHeaderDesktopV1Attributes } from '@/features/spot-header/spot-header-desktop-v1/spot-header-desktop-v1-type';

import React, { useState } from 'react';

import DividerHorizontalDesktop from '@/core/components/divider/divider-horizontal-desktop';
import InfoText from '@/core/components/info-text/info-text';
import { Badges, Button, CtaListOverlay } from '@/core/features';
import useSticky from '@/core/features/scroll/use-sticky';
import useLinkoutTracking from '@/features/linkout-tracking/use-linkout-tracking';
import HeaderMapButtonDesktop from '@/features/map/header-map-button-desktop/header-map-button-desktop';

export type SpotHeaderDesktopV1Props = PageComponentProps<SpotHeaderDesktopV1Attributes, PageComponentMetaWithPosition>;

export default function SpotHeaderDesktopV1({ attributes, meta }: SpotHeaderDesktopV1Props) {
    const { ref: stickyRef, stickyStyle } = useSticky(meta.position === 'sticky' ? 'SpotHeaderDesktopV1' : null);

    const { activity, badges, ctaOverlay, ctaText, headline, infoBar, linkCta, mapButton, subHeadline } = attributes;
    const [isCtaOverlayOpen, setCtaOverlayOpen] = useState(false);
    const { trackLinkout } = useLinkoutTracking();

    // UPDATE: make subHeadline nullable [bc-13]
    const withSubHeadline = subHeadline?.length > 0;

    return (
        <>
            <div
                className={'width-100 -margin-top-10'}
                data-scroll-padding={meta.position === 'sticky'}
                ref={stickyRef}
                style={{ ...stickyStyle }}
            >
                <div
                    className={'height-100 absolute bg-white'}
                    // White backdrop to mask scrolling elements (e.g., swiper navigation buttons) that overlap the sticky header or content area boundaries.
                    // eslint-disable-next-line no-inline-styles/no-inline-styles
                    style={{ transform: 'translateX(-50%)', width: '200vw', zIndex: -1 }}
                />
                <div
                    className={
                        'flex-space-between-horizontal padding-top-15 padding-bottom-15 gap-30 align-center nowrap bg-white'
                    }
                >
                    <div className={'font-size-24'}>
                        <h1 className={'width-100 break-anywhere bold'}>{headline}</h1>
                        <div
                            className={'column-gap-22 row-gap-8 flex-center width-100 justify-start wrap'}
                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                            style={{ gap: '8px 22px', marginTop: !withSubHeadline ? '4px' : 'unset' }}
                        >
                            {withSubHeadline && <h2 className={'break-anywhere'}>{subHeadline}</h2>}
                            <Badges
                                badges={badges}
                                fontSize={14}
                                marginBottom={-2}
                                qaId={'qa-spot-header-desktop'}
                                type={'horizontal'}
                            />
                        </div>
                    </div>
                    <div className={'flex-center gap-40'}>
                        {mapButton && (
                            <HeaderMapButtonDesktop
                                iconUrl={mapButton.iconUrl}
                                linkUrl={mapButton.linkUrl ?? ''}
                                title={mapButton.title}
                                width={'120px'}
                            />
                        )}
                        <div
                            className={'font-size-18 text-white'}
                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                            style={{ minWidth: '280px' }}
                        >
                            {ctaOverlay ? (
                                <Button
                                    fontSize={18}
                                    fontWeight={'bold'}
                                    onClick={() => setCtaOverlayOpen(true)}
                                    title={ctaText}
                                />
                            ) : (
                                <Button
                                    fontSize={18}
                                    fontWeight={'bold'}
                                    href={linkCta}
                                    linkType={'vertical'}
                                    onClick={() =>
                                        trackLinkout({ activity, elementType: 'accommodation_cta', linkUrl: linkCta })
                                    }
                                    title={ctaText}
                                />
                            )}
                        </div>
                    </div>
                </div>
                {infoBar && (
                    <InfoText
                        backgroundColor={infoBar.backgroundColor}
                        fontSize={'14px'}
                        icon={infoBar.iconUrl}
                        iconHeight={18}
                        iconWidth={18}
                        padding={'10px 20px'}
                        text={infoBar.htmlText}
                        textColor={infoBar.textColor}
                    />
                )}
                <DividerHorizontalDesktop
                    color={'alto'}
                    marginBottom={0}
                    marginTop={0}
                />
            </div>

            {isCtaOverlayOpen && !!ctaOverlay && (
                <CtaListOverlay
                    {...ctaOverlay}
                    linkTarget={'_blank'}
                    linkType={'vertical'}
                    onClick={(_event, cta) =>
                        trackLinkout({ activity, elementType: 'accommodation_cta_overlay', verticalType: cta.type })
                    }
                    onClose={() => setCtaOverlayOpen(false)}
                />
            )}
        </>
    );
}
